import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ChevronLeft, ChevronRight, Heart, Home, MapPin, 
  Bed, Bath, Square, DollarSign, Sparkles, User,
  TrendingUp, Zap, Building, TreePine
} from "lucide-react";
import { VibeTag, getAllVibeTagsArray } from "@/components/VibeTag";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Pillar Score Component
const PillarScore = ({ label, score, icon: Icon, color }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="text-center">
      <p className="text-lg font-bold text-white">{score}%</p>
      <p className="text-xs text-white/70">{label}</p>
    </div>
  </div>
);

// Main Hero Carousel Slide
const HeroSlide = ({ property, isActive, onViewProperty }) => {
  const pillarIcons = {
    emotional: { icon: Heart, color: "bg-rose-500" },
    technical: { icon: Zap, color: "bg-blue-500" },
    urban: { icon: Building, color: "bg-purple-500" },
    financial: { icon: TrendingUp, color: "bg-green-500" }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isActive ? 1 : 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className={`absolute inset-0 ${isActive ? 'z-10' : 'z-0'}`}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${property.images?.[0] || '/placeholder-property.jpg'})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
      </div>

      {/* Content Overlay */}
      <div className="relative h-full flex flex-col justify-end p-6 md:p-10 lg:p-16">
        <div className="max-w-4xl">
          {/* Match Score Badge */}
          <div className="flex items-center gap-4 mb-4">
            <Badge className="bg-primary text-[#384451] text-lg px-4 py-2 font-bold">
              <Heart className="w-5 h-5 mr-2 fill-current" />
              {property.match_score}% Match
            </Badge>
            {property.agency_name && (
              <Badge variant="outline" className="text-white border-white/50">
                {property.agency_name}
              </Badge>
            )}
          </div>

          {/* Title and Location */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 drop-shadow-lg">
            {property.title}
          </h2>
          <p className="text-lg text-white/90 flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5" /> {property.municipality}
          </p>

          {/* Features */}
          {property.features && (
            <div className="flex flex-wrap items-center gap-4 mb-6 text-white/80">
              {property.features.bedrooms && (
                <span className="flex items-center gap-2">
                  <Bed className="w-5 h-5" /> {property.features.bedrooms} Recámaras
                </span>
              )}
              {property.features.bathrooms && (
                <span className="flex items-center gap-2">
                  <Bath className="w-5 h-5" /> {property.features.bathrooms} Baños
                </span>
              )}
              {(property.features.land_area_m2 || property.features.area) && (
                <span className="flex items-center gap-2">
                  <Square className="w-5 h-5" /> {property.features.land_area_m2 || property.features.area} m²
                </span>
              )}
              {property.features.price && (
                <span className="flex items-center gap-2 text-primary font-bold">
                  <DollarSign className="w-5 h-5" /> ${(property.features.price / 1000000).toFixed(1)}M MXN
                </span>
              )}
            </div>
          )}

          {/* 4 Pillar Scores */}
          <div className="flex flex-wrap items-center gap-6 mb-6">
            {property.pillar_scores && Object.entries(property.pillar_scores).map(([key, data]) => (
              <PillarScore 
                key={key}
                label={data.label}
                score={data.score}
                icon={pillarIcons[key]?.icon || Heart}
                color={pillarIcons[key]?.color || "bg-slate-500"}
              />
            ))}
          </div>

          {/* Vibe Tags */}
          {property.vibe_tags && property.vibe_tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {property.vibe_tags.map(tagId => {
                const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                return tag ? <VibeTag key={tagId} tag={tag} size="sm" /> : null;
              })}
            </div>
          )}

          {/* CTA Button */}
          <Button 
            size="lg" 
            className="bg-primary hover:bg-[#b8c100] text-[#384451] font-bold px-8"
            onClick={() => onViewProperty(property)}
            data-testid={`view-property-${property.id}`}
          >
            <Sparkles className="w-5 h-5 mr-2" /> Ver Propiedad y Hacer Match
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Thumbnail Card
const ThumbnailCard = ({ property, isActive, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.98 }}
    className={`relative cursor-pointer rounded-xl overflow-hidden flex-shrink-0 transition-all duration-300 ${
      isActive ? 'ring-4 ring-primary' : 'ring-2 ring-transparent hover:ring-white/50'
    }`}
    style={{ width: '200px', height: '140px' }}
    onClick={onClick}
    data-testid={`thumbnail-${property.id}`}
  >
    <img 
      src={property.images?.[0] || '/placeholder-property.jpg'} 
      alt={property.title}
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-3">
      <p className="text-white text-sm font-medium truncate">{property.title}</p>
      <div className="flex items-center justify-between mt-1">
        <span className="text-white/80 text-xs flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {property.municipality}
        </span>
        <Badge className="bg-primary/90 text-[#384451] text-xs px-2 py-0.5">
          {property.match_score}%
        </Badge>
      </div>
    </div>
  </motion.div>
);

// Main Featured Properties Carousel Component
export const FeaturedPropertiesCarousel = ({ onViewProperty, onRegister }) => {
  const [properties, setProperties] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);
  const thumbnailsRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Fetch properties
  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/featured-properties?limit=10`);
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error("Error fetching featured properties:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
    // Refresh every 2 minutes to check for new properties
    const refreshInterval = setInterval(fetchProperties, 120000);
    return () => clearInterval(refreshInterval);
  }, [fetchProperties]);

  // Auto-play carousel
  useEffect(() => {
    if (autoPlay && properties.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % properties.length);
      }, 5000); // Change slide every 5 seconds
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [autoPlay, properties.length]);

  // Navigation handlers
  const goToSlide = (index) => {
    setCurrentIndex(index);
    setAutoPlay(false);
    // Resume autoplay after 10 seconds of inactivity
    setTimeout(() => setAutoPlay(true), 10000);
  };

  const goToPrev = () => {
    goToSlide(currentIndex > 0 ? currentIndex - 1 : properties.length - 1);
  };

  const goToNext = () => {
    goToSlide((currentIndex + 1) % properties.length);
  };

  // Scroll thumbnails when current index changes
  useEffect(() => {
    if (thumbnailsRef.current) {
      const thumbnailWidth = 216; // 200px + gap
      thumbnailsRef.current.scrollTo({
        left: currentIndex * thumbnailWidth - thumbnailsRef.current.offsetWidth / 2 + thumbnailWidth / 2,
        behavior: 'smooth'
      });
    }
  }, [currentIndex]);

  // Handle property click
  const handlePropertyClick = (property) => {
    if (onViewProperty) {
      onViewProperty(property);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-[600px] bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/80">Cargando propiedades destacadas...</p>
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="w-full h-[400px] bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Home className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <p className="text-white/80">No hay propiedades destacadas disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-900" data-testid="featured-carousel">
      {/* Hero Carousel */}
      <div className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] overflow-hidden">
        <AnimatePresence mode="wait">
          {properties.map((property, index) => (
            <HeroSlide 
              key={property.id}
              property={property}
              isActive={index === currentIndex}
              onViewProperty={handlePropertyClick}
            />
          ))}
        </AnimatePresence>

        {/* Navigation Arrows */}
        {properties.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Slide Indicators */}
        <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {properties.slice(0, 10).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Ir a propiedad ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails Carousel */}
      <div className="relative px-4 py-6 bg-slate-800">
        <h3 className="text-white text-lg font-semibold mb-4 px-2">
          Más Propiedades Destacadas
        </h3>
        
        <div 
          ref={thumbnailsRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {properties.slice(0, 10).map((property, index) => (
            <ThumbnailCard
              key={property.id}
              property={property}
              isActive={index === currentIndex}
              onClick={() => {
                goToSlide(index);
                handlePropertyClick(property);
              }}
            />
          ))}
          
          {/* "Ver más" card */}
          {properties.length >= 4 && (
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="flex-shrink-0 rounded-xl overflow-hidden cursor-pointer bg-gradient-to-br from-primary to-[#b8c100] flex items-center justify-center"
              style={{ width: '200px', height: '140px' }}
              onClick={onRegister}
              data-testid="see-more-properties"
            >
              <div className="text-center p-4">
                <Sparkles className="w-8 h-8 text-[#384451] mx-auto mb-2" />
                <p className="text-[#384451] font-bold">Ver más propiedades</p>
                <p className="text-[#384451]/70 text-sm">Regístrate gratis</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedPropertiesCarousel;
